import React from "react";
import { useTranslation } from "react-i18next";
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";

const formatTime = (time) => (time ? time.substring(0, 5) : "-");

const formatDate = (dateStr, language) =>
  new Intl.DateTimeFormat(language, { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(dateStr));

const AttendanceDetailDialog = ({ open, onOpenChange, selectedAttendance, calculationDetail, employees }) => {
  const { t, i18n } = useTranslation();
  const getEmployeeName = (id) => {
    const emp = employees.find((e) => e.id === id);
    return emp ? `${emp.last_name} ${emp.first_name}` : t("attComp.empInconnu");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t("attComp.detail.title")}</DialogTitle>
          <DialogDescription>
            {selectedAttendance && (
              <>{getEmployeeName(selectedAttendance.employee_id)} — {formatDate(selectedAttendance.date, i18n.language)}</>
            )}
          </DialogDescription>
        </DialogHeader>

        {calculationDetail && (
          <div className="space-y-4 py-4">
            <div className="flex items-center justify-center gap-3 p-4 bg-muted/50 rounded-lg">
              {calculationDetail.status_color === "red" ? (
                <TrendingDown className="h-8 w-8 text-red-600" />
              ) : calculationDetail.status_color === "green" ? (
                <CheckCircle2 className="h-8 w-8 text-green-600" />
              ) : (
                <AlertCircle className="h-8 w-8 text-orange-600" />
              )}
              <div>
                <p className="font-semibold text-lg">{calculationDetail.status_label}</p>
                <p className="text-sm text-muted-foreground">{calculationDetail.message}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-sm">{t("attComp.detail.expectedSchedule")}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("attComp.detail.arrival")}</span>
                      <span className="font-medium">{formatTime(calculationDetail.expected_start)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("attComp.detail.departure")}</span>
                      <span className="font-medium">{formatTime(calculationDetail.expected_end)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-3"><CardTitle className="text-sm">{t("attComp.detail.realSchedule")}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("attComp.detail.arrival")}</span>
                      <span className="font-medium">{formatTime(calculationDetail.actual_start)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{t("attComp.detail.departure")}</span>
                      <span className="font-medium">{formatTime(calculationDetail.actual_end)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium">{t("attComp.detail.calcDetails")}</h4>

              {calculationDetail.late_minutes > 0 && (
                <div className="flex items-start gap-2 p-3 bg-red-950/20 border border-red-900/30 rounded">
                  <TrendingDown className="h-4 w-4 text-red-400 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-foreground">{t("attComp.detail.lateArrival")}</p>
                    <p className="text-muted-foreground mt-1">{t("attComp.detail.lateMinutes", { count: calculationDetail.late_minutes })}</p>
                    <p className="text-muted-foreground text-xs mt-1">{t("attComp.detail.tolerance", { min: calculationDetail.late_tolerance })}</p>
                    {calculationDetail.late_minutes_after_tolerance > 0 && (
                      <p className="text-red-400 font-medium mt-1">
                        {t("attComp.detail.lateAfterTolerance", { count: calculationDetail.late_minutes_after_tolerance })}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {calculationDetail.early_departure_minutes > 0 && (
                <div className="flex items-start gap-2 p-3 bg-orange-950/20 border border-orange-900/30 rounded">
                  <TrendingUp className="h-4 w-4 text-orange-400 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-foreground">{t("attComp.detail.earlyDeparture")}</p>
                    <p className="text-muted-foreground mt-1">{t("attComp.detail.earlyMinutes", { count: calculationDetail.early_departure_minutes })}</p>
                    <p className="text-muted-foreground text-xs mt-1">{t("attComp.detail.tolerance", { min: calculationDetail.early_tolerance })}</p>
                  </div>
                </div>
              )}

              {calculationDetail.within_tolerance && (
                <div className="flex items-start gap-2 p-3 bg-green-950/20 border border-green-900/30 rounded">
                  <CheckCircle2 className="h-4 w-4 text-green-400 mt-0.5" />
                  <div className="flex-1 text-sm">
                    <p className="font-medium text-foreground">{t("attComp.detail.withinTolerance")}</p>
                    <p className="text-muted-foreground mt-1">{t("attComp.detail.withinToleranceDesc")}</p>
                  </div>
                </div>
              )}
            </div>

            {calculationDetail.schedule_name && (
              <div className="text-sm text-muted-foreground bg-muted/50 p-3 rounded">
                <span className="font-medium">{t("attComp.detail.scheduleLabel")}</span> {calculationDetail.schedule_name}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("common.close")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AttendanceDetailDialog;
